import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, paymentModeToAccount } from '../../api/accounts';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import BranchSelectModal from '../../components/common/BranchSelectModal';
import { computeFifoAllocation, getInvoicePaidAmount } from '../../api/receiptAllocations';

export default function ReceiptVoucherForm() {
  const navigate = useNavigate();
  const { user, branchId, role } = useAuth();
  const isSadmin = role === 'sadmin';
  const username = user?.email?.split('@')[0] || 'unknown';

  const CHEQUES_IN_HAND_ID = ACCOUNTS.CHEQUES_IN_HAND;

  // 'client' (default, existing flow) or 'suspense' — for a bank deposit that arrived
  // but whose depositor can't be identified yet. Posted to the Suspense account (2015)
  // instead of a client's AR/deposit, and reclassified later from the Suspense Register
  // once staff work out who it belongs to.
  const [receiptType, setReceiptType] = useState('client');
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [clientLabel, setClientLabel] = useState('');
  const [form, setForm] = useState({
    voucher_date: new Date().toISOString().slice(0, 10),
    received_from: '',
    client_id: '',
    received_into_account_id: '',
    income_account_id: '',
    amount: '',
    currency_id: '',
    exchange_rate: '1',
    payment_mode: '',
    bank_reference: '',
    description: '',
  });
  const [linkedQuotation, setLinkedQuotation] = useState(null);
  const [openItems, setOpenItems] = useState(null); // { invoices: [], quotations: [] }
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null); // manual override — pin this receipt to one invoice
  const [fifoAllocations, setFifoAllocations] = useState([]); // computed FIFO split: old balance -> invoices -> advance
  const [fifoLoading, setFifoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchConfirmed, setBranchConfirmed] = useState(false);

  useEffect(() => {
    supabase.from('chart_of_accounts').select('id, account_code, account_name, account_types(category)').eq('is_active', true).order('account_code')
      .then(({ data }) => setAccounts((data || []).map(a => ({ ...a, category: a.account_types?.category || '' }))));
    supabase.from('currencies').select('id, code, name').eq('is_active', true).order('code')
      .then(({ data }) => { setCurrencies(data || []); const lkr = (data || []).find(c => c.code === 'LKR'); if (lkr) set('currency_id', lkr.id); });
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name')
      .then(({ data }) => setBranches(data || []));
  }, []);

  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  // Auto-compute FIFO allocation (old balance -> invoices oldest-first -> advance) whenever
  // client/amount changes, unless the receipt is explicitly linked to a pro forma instead.
  // If staff has clicked a specific invoice, that invoice is paid first (capped at its
  // outstanding balance) and only the remainder — if any — falls through to FIFO.
  useEffect(() => {
    const modeVal = (form.payment_mode || '').toUpperCase();
    const isSpecial = modeVal === 'OPENING BALANCE' || modeVal === 'CANCELLATION FEE';
    if (!form.client_id || !form.amount || linkedQuotation || isSpecial) { setFifoAllocations([]); return; }
    let cancelled = false;
    setFifoLoading(true);
    const amt = parseFloat(form.amount) || 0;
    const selectedInv = selectedInvoiceId ? openItems?.invoices.find(i => i.id === selectedInvoiceId) : null;
    (async () => {
      if (!selectedInv) {
        return computeFifoAllocation(form.client_id, amt);
      }
      const manualAmount = Math.min(amt, selectedInv.balance);
      const manualAlloc = { target_type: 'invoice', invoice_id: selectedInv.id, label: selectedInv.invoice_number, amount: manualAmount };
      const remainder = amt - manualAmount;
      if (remainder <= 0.01) return [manualAlloc];
      const rest = await computeFifoAllocation(form.client_id, remainder, [selectedInv.id]);
      return [manualAlloc, ...rest];
    })()
      .then(allocs => { if (!cancelled) setFifoAllocations(allocs); })
      .finally(() => { if (!cancelled) setFifoLoading(false); });
    return () => { cancelled = true; };
  }, [form.client_id, form.amount, form.payment_mode, linkedQuotation, selectedInvoiceId, openItems]);

  async function loadOpenItems(clientId) {
    if (!clientId) { setOpenItems(null); return; }
    setLoadingItems(true);
    const [{ data: invData }, { data: quotData }] = await Promise.all([
      supabase.from('invoices')
        .select('id, invoice_number, issued_date, status, line_items')
        .eq('client_id', clientId)
        .in('status', ['DRAFT', 'SENT'])
        .order('issued_date', { ascending: false })
        .limit(20),
      supabase.from('quotations')
        .select('id, quotation_number, created_at, status')
        .eq('client_id', clientId)
        .not('status', 'eq', 'CANCELLED')
        .order('created_at', { ascending: false })
        .limit(10),
    ]);

    // Compute invoice totals and subtract everything already paid (allocations + legacy receipts)
    const invoices = (await Promise.all((invData || []).map(async inv => {
      const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
      const paid = await getInvoicePaidAmount(inv.id);
      const balance = total - paid;
      return { ...inv, total, paid, balance };
    }))).filter(i => i.balance > 0.01);

    setOpenItems({ invoices, quotations: quotData || [] });
    setLoadingItems(false);
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const debitAccount = accounts.find(a => a.id === form.received_into_account_id);
  const arAccount = accounts.find(a => a.id === ACCOUNTS.AR); // always the AR control account
  const depositAccount = accounts.find(a => a.id === ACCOUNTS.CLIENT_DEPOSITS);
  const suspenseAccount = accounts.find(a => a.id === ACCOUNTS.SUSPENSE);
  const currency = currencies.find(c => c.id === form.currency_id);

  const mode = (form.payment_mode || '').toUpperCase();
  const isOpeningBalance = mode === 'OPENING BALANCE';
  const isCancellationFee = mode === 'CANCELLATION FEE';
  const isSpecialMode = isOpeningBalance || isCancellationFee;
  // For special modes: preview DR AR / CR Equity or Other Income (no bank account needed)
  const previewDebitAcc = receiptType === 'suspense' ? debitAccount : isSpecialMode ? arAccount : debitAccount;
  const amtNum = parseFloat(form.amount) || 0;
  const arPortionPreview = fifoAllocations.filter(a => a.target_type !== 'advance').reduce((s, a) => s + a.amount, 0);
  const depositPortionPreview = fifoAllocations.filter(a => a.target_type === 'advance').reduce((s, a) => s + a.amount, 0);
  // What will actually post on the credit side, mirroring handleSubmit exactly
  const previewCreditLines = receiptType === 'suspense'
    ? [{ account: suspenseAccount, amount: amtNum }]
    : isSpecialMode
    ? [{ account: isOpeningBalance ? accounts.find(a => a.id === ACCOUNTS.OPENING_BALANCE_EQUITY) : accounts.find(a => a.id === ACCOUNTS.OTHER_INCOME), amount: amtNum }]
    : linkedQuotation
      ? [{ account: depositAccount, amount: amtNum }]
      : [
          ...(arPortionPreview > 0 ? [{ account: arAccount, amount: arPortionPreview }] : []),
          ...((depositPortionPreview > 0 || (arPortionPreview === 0 && depositPortionPreview === 0)) ? [{ account: depositAccount, amount: depositPortionPreview || amtNum }] : []),
        ];

  // The journal posts before the voucher row is inserted. If that insert then fails, the
  // journal is left with no voucher, and every retry posts another one — this is how cheque
  // 217283 ended up debited to Cheques in Hand four times (JE-9054/9055/9056 + JE-9144).
  // Reverse the orphan so a failed save leaves the ledger exactly as it was.
  const reverseOrphan = async (je) => {
    if (je?.id) { try { await voidJournal(je.id, 'Receipt voucher save failed', username); } catch (_) { /* surfaced via the original error */ } }
  };

  const handleSubmit = async () => {
    if (receiptType === 'suspense') {
      if (!form.amount || !form.currency_id) return setError('Amount and currency are required.');
      if (!form.received_into_account_id && form.payment_mode !== 'Cheque') return setError('Select a deposit account.');
      if (!form.description.trim()) return setError('A description is required — this is how you\'ll identify this deposit later, since no client is tagged yet.');
    } else if (!form.amount || !form.currency_id || !form.client_id) {
      return setError('Client, amount and currency are required.');
    }
    const modeVal = (form.payment_mode || '').toUpperCase();
    const needsBank = modeVal !== 'OPENING BALANCE' && modeVal !== 'CANCELLATION FEE';
    if (receiptType === 'client' && needsBank && !form.received_into_account_id && form.payment_mode !== 'Cheque') {
      return setError('Select a deposit account.');
    }
    if (receiptType === 'client' && needsBank && !linkedQuotation && fifoLoading) {
      return setError('Still calculating how this receipt applies — please wait a moment and try again.');
    }
    setSaving(true); setError('');
    try {
      const amt = parseFloat(form.amount);
      const mode = (form.payment_mode || '').toUpperCase();

      if (receiptType === 'suspense') {
        const bankAccount = form.received_into_account_id || paymentModeToAccount(form.payment_mode);
        const je = await postJournal({
          date: form.voucher_date,
          description: form.description,
          reference_type: 'RECEIPT_VOUCHER',
          currency_id: form.currency_id,
          exchange_rate: parseFloat(form.exchange_rate) || 1,
          created_by: username,
          lines: [
            { account_id: bankAccount, debit_amount: amt, credit_amount: 0, description: `Unidentified deposit — ${form.description}` },
            { account_id: ACCOUNTS.SUSPENSE, debit_amount: 0, credit_amount: amt, description: `Unidentified deposit — ${form.description}` },
          ],
          branch_id: selectedBranchId || null,
        });
        const voucher_number = 'RV-' + Date.now().toString().slice(-8);
        const { data: rv, error: rvErr } = await supabase.from('receipt_vouchers').insert({
          voucher_number,
          voucher_date: form.voucher_date,
          received_from: form.received_from,
          client_id: null,
          received_into_account_id: form.received_into_account_id || null,
          amount: amt,
          currency_id: form.currency_id,
          exchange_rate: parseFloat(form.exchange_rate) || 1,
          base_amount: amt * (parseFloat(form.exchange_rate) || 1),
          payment_mode: form.payment_mode,
          bank_reference: form.bank_reference,
          description: form.description,
          voucher_status: form.payment_mode === 'Cheque' ? 'IN_HAND' : 'BANKED',
          is_suspense: true,
          journal_entry_id: je.id,
          created_by: username,
          branch_id: selectedBranchId || null,
        }).select().single();
        if (rvErr) { await reverseOrphan(je); throw rvErr; }
        await supabase.from('journal_entries').update({ reference_id: rv.id }).eq('id', je.id);
        setSaved({ ...rv, je_number: je.entry_number });
        return;
      }

      // Determine journal lines based on payment mode:
      // OPENING BALANCE → DR AR / CR Opening Balance Equity
      // CANCELLATION FEE → DR AR / CR Other Income
      // Pro forma deposit (no invoice/AR to net against yet) → DR Bank / CR Client Deposits (2010)
      // Normal receipt → DR Bank, then CR AR for whatever FIFO matched to an open invoice
      // or opening balance, and CR Client Deposits (2010) for any leftover with nothing to
      // apply to yet — that portion is a liability, not an AR credit, until staff explicitly
      // apply it to an invoice later (see receiptAllocations.js applyClientDeposit).
      const clientParty = form.client_id ? { party_type: 'client', party_id: form.client_id } : {};
      let journalLines;
      if (mode === 'OPENING BALANCE') {
        journalLines = [
          { account_id: ACCOUNTS.AR,                    debit_amount: amt, credit_amount: 0,   description: 'Opening balance - AR', ...clientParty },
          { account_id: ACCOUNTS.OPENING_BALANCE_EQUITY, debit_amount: 0,   credit_amount: amt, description: 'Opening balance equity' },
        ];
      } else if (mode === 'CANCELLATION FEE') {
        journalLines = [
          { account_id: ACCOUNTS.AR,           debit_amount: amt, credit_amount: 0,   description: 'Cancellation fee - AR', ...clientParty },
          { account_id: ACCOUNTS.OTHER_INCOME, debit_amount: 0,   credit_amount: amt, description: 'Cancellation fee income' },
        ];
      } else {
        const bankAccount = form.received_into_account_id || paymentModeToAccount(form.payment_mode);
        journalLines = [
          { account_id: bankAccount, debit_amount: amt, credit_amount: 0, description: `Receipt - ${form.payment_mode || 'Bank'}` },
        ];
        if (linkedQuotation) {
          journalLines.push({ account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: 0, credit_amount: amt, description: `Deposit - ${linkedQuotation.quotation_number}`, ...clientParty });
        } else {
          const arPortion = fifoAllocations.filter(a => a.target_type !== 'advance').reduce((s, a) => s + a.amount, 0);
          const depositPortion = fifoAllocations.filter(a => a.target_type === 'advance').reduce((s, a) => s + a.amount, 0);
          if (arPortion > 0) {
            journalLines.push({ account_id: ACCOUNTS.AR, debit_amount: 0, credit_amount: arPortion, description: `AR cleared - ${form.received_from || 'Client'}`, ...clientParty });
          }
          if (depositPortion > 0 || (arPortion === 0 && depositPortion === 0)) {
            // Also covers the case FIFO produced nothing at all (e.g. brand-new client) —
            // falls back to the full amount as an unapplied deposit rather than posting nothing.
            journalLines.push({ account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: 0, credit_amount: depositPortion || amt, description: `Deposit (unapplied) - ${form.received_from || 'Client'}`, ...clientParty });
          }
        }
      }

      const je = await postJournal({
        date: form.voucher_date,
        description: form.description || `Receipt from ${form.received_from || 'Client'}`,
        reference_type: 'RECEIPT_VOUCHER',
        currency_id: form.currency_id,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        created_by: username,
        lines: journalLines,
        branch_id: selectedBranchId || null,
      });

      const voucher_number = 'RV-' + Date.now().toString().slice(-8);
      const { data: rv, error: rvErr } = await supabase.from('receipt_vouchers').insert({
        voucher_number,
        voucher_date: form.voucher_date,
        received_from: form.received_from,
        client_id: form.client_id || null,
        received_into_account_id: form.received_into_account_id || null,
        income_account_id: form.income_account_id || null,
        amount: parseFloat(form.amount),
        currency_id: form.currency_id,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        base_amount: parseFloat(form.amount) * (parseFloat(form.exchange_rate) || 1),
        payment_mode: form.payment_mode,
        bank_reference: form.bank_reference,
        description: form.description,
        voucher_status: form.payment_mode === 'Cheque' ? 'IN_HAND' : 'BANKED',
        reference_type: linkedQuotation ? 'quotation' : null,
        reference_id: linkedQuotation ? linkedQuotation.id : null,
        journal_entry_id: je.id,
        created_by: username,
        branch_id: selectedBranchId || null,
      }).select().single();

      if (rvErr) { await reverseOrphan(je); throw rvErr; }

      await supabase.from('journal_entries').update({ reference_id: rv.id }).eq('id', je.id);

      // Write the FIFO allocation trail (old balance -> invoices -> advance) and
      // auto-mark any fully-covered invoice as PAID.
      if (!linkedQuotation && fifoAllocations.length) {
        const rows = fifoAllocations.map(a => ({
          receipt_voucher_id: rv.id,
          client_id: form.client_id,
          invoice_id: a.invoice_id,
          target_type: a.target_type,
          amount: a.amount,
          created_by: username,
        }));
        await supabase.from('receipt_allocations').insert(rows);

        for (const a of fifoAllocations) {
          if (a.target_type !== 'invoice' || !a.invoice_id) continue;
          const { data: invRow } = await supabase.from('invoices').select('line_items').eq('id', a.invoice_id).single();
          const invTotal = (invRow?.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price || li.unitPrice || 0) * parseFloat(li.qty || li.quantity || 1)), 0);
          const totalPaid = await getInvoicePaidAmount(a.invoice_id);
          if (invTotal > 0 && totalPaid >= invTotal - 0.01) {
            await supabase.from('invoices').update({ status: 'PAID' }).eq('id', a.invoice_id);
          }
        }
      }

      setSaved({ ...rv, je_number: je.entry_number });
    } catch (err) {
      setError(err.message || 'Error saving voucher');
      setSaving(false);
    }
  };

  const resetForm = () => { setSaved(null); setLinkedQuotation(null); setSelectedInvoiceId(null); setFifoAllocations([]); setOpenItems(null); setClientLabel(''); setReceiptType('client'); setForm({ voucher_date: new Date().toISOString().slice(0, 10), received_from: '', client_id: '', received_into_account_id: '', income_account_id: '', amount: '', currency_id: '', exchange_rate: '1', payment_mode: '', bank_reference: '', description: '' }); setSaving(false); };

  if (saved) return (
    <div>
      <div className="page-header"><div className="page-title">Receipt Voucher Posted</div></div>
      <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{saved.voucher_number}</div>
        <div style={{ color: 'var(--text-muted)', marginBottom: 6 }}>Journal Entry: <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{saved.je_number}</span></div>
        <div style={{ color: 'var(--text-muted)', marginBottom: saved.is_suspense ? 6 : 24 }}>Amount: {currency?.code} {parseFloat(saved.amount).toLocaleString()}</div>
        {saved.is_suspense && (
          <div style={{ color: '#b45309', background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '10px 14px', marginBottom: 24, fontSize: 13, display: 'inline-block' }}>
            Posted to Suspense — no client identified yet. Reclassify it once you know who this belongs to from the <a href="/vouchers/suspense" style={{ color: '#b45309', fontWeight: 700 }}>Suspense Register</a>.
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => navigate(`/vouchers/receipt/${saved.id}`)}>Print / View</button>
          <button className="btn btn-secondary" onClick={resetForm}>+ New Receipt Voucher</button>
          <button className="btn btn-secondary" onClick={() => navigate('/vouchers/receipt')}>View All</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!branchConfirmed && (
        <BranchSelectModal
          defaultBranchId={selectedBranchId || branchId}
          onConfirm={(id) => { setSelectedBranchId(id); setBranchConfirmed(true); }}
        />
      )}
      <div className="page-header">
        <div><div className="page-title">Receipt Voucher</div><div className="page-subtitle">Money coming in — auto posts journal entry</div></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || (receiptType === 'client' && !linkedQuotation && fifoLoading)}>{saving ? 'Posting...' : (receiptType === 'client' && fifoLoading && !linkedQuotation) ? 'Calculating…' : 'Post Receipt'}</button>
          </div>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}

      <div className="card">
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
          {[['client', 'Client Receipt'], ['suspense', 'Unidentified Deposit']].map(([key, label]) => (
            <button key={key} onClick={() => setReceiptType(key)} style={{ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: receiptType === key ? 700 : 400, color: receiptType === key ? 'var(--primary)' : 'var(--text-muted)', borderBottom: receiptType === key ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2, fontSize: 13 }}>
              {label}
            </button>
          ))}
        </div>
        {receiptType === 'suspense' ? (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, padding: '12px 16px', background: 'rgba(245,158,11,0.08)', borderRadius: 8, fontSize: 13 }}>
            <span style={{ color: '#b45309', fontWeight: 600 }}>UNIDENTIFIED DEPOSIT</span>
            <span style={{ color: 'var(--text-muted)' }}>
              {isSadmin ? ' DR Bank/Cash Account / CR Suspense (2015) — no client tagged; reclassify later from the Suspense Register' : ' Money received, depositor not yet identified — will be matched to a client later'}
            </span>
          </div>
        ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, padding: '12px 16px', background: isOpeningBalance ? 'rgba(79,110,247,0.07)' : isCancellationFee ? 'rgba(245,158,11,0.07)' : form.payment_mode === 'Cheque' ? 'rgba(234,179,8,0.08)' : 'rgba(34,197,94,0.05)', borderRadius: 8, fontSize: 13 }}>
          <span style={{ color: isOpeningBalance ? 'var(--primary)' : isCancellationFee ? '#b45309' : form.payment_mode === 'Cheque' ? '#854d0e' : 'var(--success)', fontWeight: 600 }}>
            {isOpeningBalance ? 'OPENING BALANCE' : isCancellationFee ? 'CANCELLATION FEE' : form.payment_mode === 'Cheque' ? 'CHEQUE RECEIVED' : 'RECEIPT IN'}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>
            {isSadmin
              ? (isOpeningBalance
                  ? ' DR Accounts Receivable / CR Opening Balance Equity — seeds client AR balance'
                  : isCancellationFee
                  ? ' DR Accounts Receivable / CR Other Income — fee owed by client'
                  : form.payment_mode === 'Cheque'
                  ? ' DR Cheques in Hand / CR Accounts Receivable — deposit to bank later'
                  : ' DR Bank/Cash Account / CR Accounts Receivable (Client)')
              : (isOpeningBalance
                  ? ' Seeds this client’s opening balance'
                  : isCancellationFee
                  ? ' Cancellation fee owed by client'
                  : form.payment_mode === 'Cheque'
                  ? ' Cheque received — deposit to bank later'
                  : ' Payment received from client')}
          </span>
        </div>
        )}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" value={form.voucher_date} onChange={e => set('voucher_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Received From</label>
            <input className="form-input" value={form.received_from} onChange={e => set('received_from', e.target.value)} placeholder="Payer name" />
          </div>
          {receiptType === 'client' && (
          <div className="form-group">
            <label className="form-label">Client *</label>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('clients')
                  .select('id, full_name, client_code')
                  .eq('is_active', true)
                  .or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`)
                  .order('full_name').limit(30);
                return data || [];
              }}
              value={clientLabel}
              displayKey="full_name"
              placeholder="Type client name or code…"
              onSelect={c => { setClientLabel(c.full_name); setForm(f => ({ ...f, client_id: c.id, received_from: c.full_name || f.received_from })); setLinkedQuotation(null); setSelectedInvoiceId(null); loadOpenItems(c.id); }}
              onClear={() => { setClientLabel(''); setForm(f => ({ ...f, client_id: '' })); setOpenItems(null); setLinkedQuotation(null); setSelectedInvoiceId(null); setFifoAllocations([]); }}
            />
          </div>
          )}
          <div className="form-group">
            <label className="form-label">Currency *</label>
            <TypeSearch
              options={currencies}
              value={currencies.find(c => c.id === form.currency_id)?.code || ''}
              displayKey="code"
              placeholder="Type currency…"
              onSelect={c => set('currency_id', c.id)}
              onClear={() => set('currency_id', '')}
              renderOption={(item) => <><span style={{ fontWeight: 600 }}>{item.code}</span><span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>{item.name}</span></>}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input className="form-input" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" style={{ fontSize: 16, fontWeight: 600 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Exchange Rate (to LKR)</label>
            <input className="form-input" type="number" step="0.0001" value={form.exchange_rate} onChange={e => set('exchange_rate', e.target.value)} />
          </div>
          {!isSpecialMode && (
            <div className="form-group">
              <label className="form-label">Deposit Into Account *</label>
              {form.payment_mode === 'Cheque' ? (
                <div className="form-input" style={{ background: '#fef9c3', color: '#854d0e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  1005 · Cheques in Hand <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 4 }}>(auto — cheques go here until deposited)</span>
                </div>
              ) : (
                <TypeSearch
                  options={accounts.filter(a => a.category === 'ASSET')}
                  value={accounts.find(a => a.id === form.received_into_account_id)?.account_name || ''}
                  displayKey="account_name"
                  placeholder="Type account name…"
                  onSelect={a => set('received_into_account_id', a.id)}
                  onClear={() => set('received_into_account_id', '')}
                />
              )}
            </div>
          )}
          {receiptType === 'client' && (
          <div className="form-group">
            <label className="form-label">Accounts Receivable</label>
            <div className="form-input" style={{ background: 'var(--bg)', color: 'var(--text-muted)', fontSize: 13 }}>
              1030 · Accounts Receivable <span style={{ fontSize: 11, marginLeft: 4 }}>(control account — all clients)</span>
            </div>
          </div>
          )}
          <div className="form-group">
            <label className="form-label">Payment Mode</label>
            <select className="form-input" value={form.payment_mode} onChange={e => {
              const mode = e.target.value;
              setForm(f => ({
                ...f,
                payment_mode: mode,
                received_into_account_id: mode === 'Cheque' ? CHEQUES_IN_HAND_ID : f.received_into_account_id === CHEQUES_IN_HAND_ID ? '' : f.received_into_account_id,
              }));
            }}>
              <option value="">-- Select --</option>
              <option value="Cash">Cash</option>
              <option value="Cheque">Cheque</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Card">Card</option>
              <option value="Online">Online</option>
              {receiptType === 'client' && <option value="Opening Balance">Opening Balance{isSadmin ? ' (DR AR / CR Equity)' : ''}</option>}
              {receiptType === 'client' && <option value="Cancellation Fee">Cancellation Fee{isSadmin ? ' (DR AR / CR Other Income)' : ''}</option>}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Bank Reference / Cheque No</label>
            <input className="form-input" value={form.bank_reference} onChange={e => set('bank_reference', e.target.value)} placeholder={receiptType === 'suspense' ? 'Anything on the bank statement that might help identify it' : ''} />
          </div>
          <div className="form-group full-width">
            <label className="form-label">Description / Narration{receiptType === 'suspense' ? ' *' : ''}</label>
            <textarea className="form-textarea" rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder={receiptType === 'suspense' ? 'Required — this is the only way you\'ll identify this deposit later. Note anything distinctive: amount pattern, timing, who might have sent it, etc.' : 'What is this receipt for?'} />
          </div>
        </div>

        {/* Outstanding items — click an invoice to pin this receipt to it; unpicked amounts fall through to FIFO below */}
        {form.client_id && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Outstanding Items
            </div>
            {loadingItems ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
            ) : (!openItems || (openItems.invoices.length === 0 && openItems.quotations.length === 0)) ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No outstanding invoices or pro formas for this client.</div>
            ) : (
              <div>
                {openItems.invoices.length > 0 && (
                  <div>
                    <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', background: '#fafafa', borderBottom: '1px solid var(--border)' }}>INVOICES — click one to direct this payment there (otherwise FIFO, oldest first)</div>
                    {openItems.invoices.map(inv => {
                      const selected = selectedInvoiceId === inv.id;
                      return (
                      <div key={inv.id} onClick={() => { setSelectedInvoiceId(selected ? null : inv.id); setLinkedQuotation(null); }} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 16px', cursor: 'pointer', fontSize: 13,
                        background: selected ? 'rgba(34,197,94,0.08)' : 'white',
                        borderBottom: '1px solid var(--border)',
                        borderLeft: selected ? '3px solid var(--success)' : '3px solid transparent',
                      }}>
                        <div>
                          <span style={{ fontWeight: 600 }}>{inv.invoice_number}</span>
                          <span style={{ marginLeft: 10, color: 'var(--text-muted)', fontSize: 12 }}>{inv.issued_date?.slice(0, 10)}</span>
                          {selected && <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>✓ Selected</span>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {inv.paid > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total: {inv.total.toLocaleString()} · Paid: {inv.paid.toLocaleString()}</div>}
                          <div style={{ fontWeight: 700, color: 'var(--danger)' }}>Balance: LKR {inv.balance.toLocaleString()}</div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
                {openItems.quotations.length > 0 && (
                  <div>
                    <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', background: '#fafafa', borderBottom: '1px solid var(--border)' }}>PRO FORMAS / QUOTATIONS (ADVANCE) — click to record this receipt as an advance instead</div>
                    {openItems.quotations.map(q => {
                      const selected = linkedQuotation?.id === q.id;
                      return (
                        <div key={q.id} onClick={() => { setLinkedQuotation(selected ? null : q); setSelectedInvoiceId(null); }} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 16px', cursor: 'pointer', fontSize: 13,
                          background: selected ? 'rgba(34,197,94,0.08)' : 'white',
                          borderBottom: '1px solid var(--border)',
                          borderLeft: selected ? '3px solid var(--success)' : '3px solid transparent',
                        }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>{q.quotation_number}</span>
                            <span style={{ marginLeft: 10, color: 'var(--text-muted)', fontSize: 12 }}>{q.created_at?.slice(0, 10)}</span>
                            <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text-muted)' }}>{q.status}</span>
                            {selected && <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>✓ Selected (Advance)</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* FIFO allocation preview — automatic split: old balance -> invoices oldest-first -> advance */}
        {form.client_id && form.amount && !linkedQuotation && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', background: 'rgba(79,110,247,0.06)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              This receipt will be applied (FIFO — oldest debt first)
            </div>
            {fifoLoading ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Calculating…</div>
            ) : fifoAllocations.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Nothing to allocate.</div>
            ) : (
              fifoAllocations.map((a, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', fontSize: 13, borderBottom: i < fifoAllocations.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span>
                    {a.label}
                    {a.target_type === 'old_balance' && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(opening balance)</span>}
                    {a.target_type === 'advance' && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(unmatched — advance)</span>}
                  </span>
                  <span style={{ fontWeight: 700 }}>{currency?.code} {a.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))
            )}
          </div>
        )}

        {isSadmin && previewDebitAcc && previewCreditLines.length > 0 && form.amount && (
          <div style={{ marginTop: 20, padding: '14px 18px', background: 'var(--bg)', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Journal Preview</div>
            <table style={{ width: '100%' }}>
              <thead><tr>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, paddingBottom: 6 }}>Account</th>
                <th style={{ textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500 }}>DR</th>
                <th style={{ textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500 }}>CR</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 0' }}>{previewDebitAcc.account_code} · {previewDebitAcc.account_name}</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: 600 }}>{currency?.code} {parseFloat(form.amount || 0).toLocaleString()}</td>
                  <td />
                </tr>
                {previewCreditLines.map((l, i) => l.account && (
                  <tr key={i}>
                    <td style={{ padding: '4px 0' }}>{l.account.account_code} · {l.account.account_name}</td>
                    <td />
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>{currency?.code} {l.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
